import { useRef, useState, type DragEvent, type ReactNode } from "react";
import Checkmark from "../../components/Atomos/Icons/Checkmark";
import Close from "../../components/Atomos/Icons/Close";
import { toastError } from "../../utils/toast";

const MAX_FILE_SIZE_MB = 500;
const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;

type HelpCenterEditUploadBoxProps = {
  label: string;
  icon: ReactNode;
  accept?: string;
  previewUrl?: string;
  fileName?: string;
  previewMode?: "image" | "file";
  fileKind?: "video" | "pdf";
  showHint?: boolean;
  labelPlacement?: "inside" | "below";
  dropzoneClassName?: string;
  onFileSelected: (file: File) => void | Promise<void>;
  onClear?: () => void;
};

export function fileNameFromUrl(url?: string): string | undefined {
  if (!url?.trim()) {
    return undefined;
  }

  try {
    const pathname = new URL(url, window.location.origin).pathname;
    const name = decodeURIComponent(pathname.split("/").pop() ?? "");
    return name || undefined;
  } catch {
    const name = url.split("/").pop();
    return name ? decodeURIComponent(name) : undefined;
  }
}

export default function HelpCenterEditUploadBox({
  label,
  icon,
  accept,
  previewUrl,
  fileName,
  previewMode = "image",
  fileKind,
  showHint = true,
  labelPlacement = "inside",
  dropzoneClassName = "",
  onFileSelected,
  onClear,
}: HelpCenterEditUploadBoxProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [pendingFileName, setPendingFileName] = useState<string | null>(null);
  const [pendingPreviewUrl, setPendingPreviewUrl] = useState<string | null>(
    null,
  );
  const hasImagePreview = previewMode === "image" && Boolean(previewUrl);
  const displayImageUrl = previewUrl ?? pendingPreviewUrl ?? undefined;
  const showImagePreview = previewMode === "image" && Boolean(displayImageUrl);
  const resolvedFileName = fileName ?? fileNameFromUrl(previewUrl);
  const hasFilePreview =
    previewMode === "file" && Boolean(resolvedFileName || previewUrl);
  const canClear =
    Boolean(onClear && (previewUrl || resolvedFileName)) && !isUploading;

  const handleFile = async (file: File | undefined) => {
    if (!file || isUploading) {
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      toastError(`File must be ${MAX_FILE_SIZE_MB} MB or smaller.`);
      return;
    }

    let localPreviewUrl: string | null = null;
    if (previewMode === "image") {
      localPreviewUrl = URL.createObjectURL(file);
      setPendingPreviewUrl(localPreviewUrl);
    }

    setPendingFileName(file.name);
    setIsUploading(true);

    try {
      await onFileSelected(file);
    } finally {
      setIsUploading(false);
      setPendingFileName(null);
      if (localPreviewUrl) {
        URL.revokeObjectURL(localPreviewUrl);
      }
      setPendingPreviewUrl(null);
    }
  };

  const handleDrop = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setIsDragging(false);
    handleFile(event.dataTransfer.files?.[0]);
  };

  const labelBelow = labelPlacement === "below";
  const uploadedFileLabel =
    fileKind === "video"
      ? "Video uploaded"
      : fileKind === "pdf"
        ? "PDF uploaded"
        : "File uploaded";

  return (
    <div
      className={`help-center-edit-upload-box${
        labelBelow ? " help-center-edit-upload-box-label-below" : ""
      }`}
    >
      <div className="help-center-edit-upload-dropzone-wrap">
        <button
          type="button"
          className={`help-center-edit-upload-dropzone${dropzoneClassName ? ` ${dropzoneClassName}` : ""}${
            isDragging ? " help-center-edit-upload-dropzone-active" : ""
          }${hasFilePreview ? " help-center-edit-upload-dropzone-file" : ""}${
            isUploading ? " help-center-edit-upload-dropzone-uploading" : ""
          }`}
          disabled={isUploading}
          aria-busy={isUploading}
          onClick={() => inputRef.current?.click()}
          onDragOver={(event) => {
            if (isUploading) {
              return;
            }
            event.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
        >
          {showImagePreview ? (
            <img
              src={displayImageUrl}
              alt=""
              className="help-center-edit-upload-preview"
            />
          ) : hasFilePreview ? (
            <div
              className={`help-center-edit-upload-file-state${
                fileKind ? ` help-center-edit-upload-file-state--${fileKind}` : ""
              }`}
            >
              <div className="help-center-edit-upload-file-icon-wrap" aria-hidden="true">
                {icon}
              </div>
              <span className="help-center-edit-upload-file-status">
                <Checkmark className="help-center-edit-upload-file-check" />
                {uploadedFileLabel}
              </span>
              {resolvedFileName ? (
                <span className="help-center-edit-upload-filename" title={resolvedFileName}>
                  {resolvedFileName}
                </span>
              ) : null}
            </div>
          ) : (
            <span className="help-center-edit-upload-icon" aria-hidden="true">
              {icon}
            </span>
          )}
          {!labelBelow ? (
            <span className="help-center-edit-upload-label">{label}</span>
          ) : null}
          {isUploading ? (
            <div className="help-center-edit-upload-loading-overlay">
              <span
                className="help-center-edit-upload-loading-spinner"
                aria-hidden="true"
              />
              <span className="help-center-edit-upload-loading-text">
                Uploading...
              </span>
              {pendingFileName ? (
                <span
                  className="help-center-edit-upload-loading-filename"
                  title={pendingFileName}
                >
                  {pendingFileName}
                </span>
              ) : null}
            </div>
          ) : null}
        </button>
        {canClear ? (
          <button
            type="button"
            className="help-center-edit-upload-clear"
            aria-label={`Remove ${label}`}
            onClick={(event) => {
              event.stopPropagation();
              onClear?.();
            }}
          >
            <Close className="h-4 w-4" />
          </button>
        ) : null}
      </div>
      {labelBelow ? (
        <span className="help-center-edit-upload-slot-label">{label}</span>
      ) : null}
      {showHint ? (
        <p className="help-center-edit-upload-hint">
          Click or Drag | {MAX_FILE_SIZE_MB} MB max. 300x300 pixels.
        </p>
      ) : null}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="sr-only"
        onChange={(event) => {
          handleFile(event.target.files?.[0]);
          event.target.value = "";
        }}
      />
    </div>
  );
}
